import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Check, Sparkles, SplitSquareVertical } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMOCIONES = ['Ansiedad', 'Frustración', 'Vergüenza', 'Miedo', 'Culpa', 'Tristeza', 'Irritación', 'Vacío', 'Confusión'];

export default function Modulo41Descomposicion() {
    const [situacion, setSituacion] = useState('');
    const [pensamiento, setPensamiento] = useState('');
    const [emocion, setEmocion] = useState('');
    const [emocionTexto, setEmocionTexto] = useState('');
    const [conducta, setConducta] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/descomposicion_momento`, { withCredentials: true });
                if (data.data) { const d = data.data; if (d.situacion) setSituacion(d.situacion); if (d.pensamiento) setPensamiento(d.pensamiento); if (d.emocion) setEmocion(d.emocion); if (d.emocionTexto) setEmocionTexto(d.emocionTexto); if (d.conducta) setConducta(d.conducta); }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        };
        loadDraft();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'descomposicion_momento', data: { situacion, pensamiento, emocion, emocionTexto, conducta } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [situacion, pensamiento, emocion, emocionTexto, conducta]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [situacion, pensamiento, emocion, emocionTexto, conducta, saveDraft, loadingDraft, readOnly]);

    const emocionFinal = emocionTexto.trim() || emocion;
    const canSubmit = !readOnly && (situacion.trim().length > 0 && pensamiento.trim().length > 0 && emocionFinal.length >= 3 && conducta.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) { toast.error('Completa los 4 campos con detalle suficiente'); return; }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/descomposicion-momento/submit`, { situacion, pensamiento, emocion: emocionFinal, conducta }, { withCredentials: true });
            setAiInsight(data.ai_insight); setSubmitted(true); toast.success('Módulo completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); } finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><SplitSquareVertical className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Momento descompuesto</h2>
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="descomposicion_momento" chapter={4} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    const fields = [
        { id: 'situacion', label: '¿Qué estaba pasando?', sub: 'Describe la situación concreta, sin interpretaciones', value: situacion, set: setSituacion, ph: 'Ej: Tenía que enviar el informe antes de las 5pm...' },
        { id: 'pensamiento', label: '¿Qué pensaste?', sub: 'El pensamiento que apareció en tu mente', value: pensamiento, set: setPensamiento, ph: 'Ej: "No voy a poder hacerlo bien, van a pensar que soy incompetente"' },
        { id: 'conducta', label: '¿Qué hiciste?', sub: 'La conducta que realizaste después', value: conducta, set: setConducta, ph: 'Ej: Abrí redes sociales y pospuse el informe 2 horas...' }
    ];

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Cap. 4</span><span className="text-xs text-[#7A8A93]">4.1</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Descomposición del momento</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-4 sm:p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md"><SplitSquareVertical className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-[#1F2A30] text-base sm:text-lg">Separa las piezas del momento</CardTitle><p className="text-xs sm:text-sm text-[#4A5B63]">Situación, pensamiento, emoción y conducta</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        {fields.map(f => (
                            <div key={f.id} className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E]">{f.label}</Label>
                                <p className="text-xs text-[#7A8A93]">{f.sub}</p>
                                <Textarea value={f.value} onChange={e => f.set(e.target.value)} placeholder={f.ph} className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[70px]" data-testid={`input-${f.id}`} />
                            </div>
                        ))}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">¿Qué sentiste?</Label>
                            <div className="flex flex-wrap gap-2">
                                {EMOCIONES.map(em => (
                                    <button key={em} onClick={() => { setEmocion(em); setEmocionTexto(''); }}
                                        className={`px-3 py-1.5 text-xs sm:text-sm rounded-xl transition-all ${emocion === em && !emocionTexto ? 'bg-[#3E5F6E] text-white shadow-md' : 'bg-[#F5F7F8] text-[#7A8A93] border border-[#DCE3E8]'}`}>{em}</button>
                                ))}
                            </div>
                            <input type="text" value={emocionTexto} onChange={e => setEmocionTexto(e.target.value)} placeholder="O escribe otra emoción..." className="w-full px-3 py-2 text-sm border border-[#DCE3E8] rounded-xl bg-[#F5F7F8] focus:ring-1 focus:ring-[#5FAF9D] outline-none" data-testid="input-emocion" />
                        </div>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-descomposicion">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Completar módulo</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
